/**
 * GherkinContextModal.tsx
 *
 * Modal shown before starting Gherkin generation.
 * Saved Domain Knowledge and Enterprise Knowledge are included automatically.
 * Users can optionally attach extra run-specific context files (.txt, .md, .markdown, .pdf,
 * .docx) with per-file descriptions to supplement the agent's output.
 */
import React, { useCallback, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { PlusCircle, Trash, Upload } from 'react-bootstrap-icons';

const ACCEPTED_TYPES = '.txt,.md,.markdown,.pdf,.docx';
const ACCEPTED_EXTENSIONS = new Set(['.txt', '.md', '.markdown', '.pdf', '.docx']);
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

interface ContextEntry {
  file: File;
  description: string;
}

interface GherkinContextModalProps {
  show: boolean;
  onHide: () => void;
  /** Called when user confirms generation (with or without context). */
  onSubmit: (files: File[], descriptions: string[]) => void;
  /** True while the parent component is waiting for the API response. */
  submitting?: boolean;
}

export default function GherkinContextModal({
  show,
  onHide,
  onSubmit,
  submitting = false,
}: GherkinContextModalProps): React.ReactElement {
  const [entries, setEntries] = useState<ContextEntry[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setEntries([]);
    setFileError(null);
  }, []);

  const handleHide = useCallback(() => {
    if (submitting) return;
    resetState();
    onHide();
  }, [onHide, resetState, submitting]);

  const handleFilesSelected = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setFileError(null);

    const incoming = Array.from(selectedFiles);
    const current = entries.length;
    const allowed = MAX_FILES - current;

    if (allowed <= 0) {
      setFileError(`Maximum of ${MAX_FILES} context files allowed.`);
      return;
    }

    const toAdd = incoming.slice(0, allowed);
    const unsupported = toAdd.filter((f) => !ACCEPTED_EXTENSIONS.has(`.${f.name.split('.').pop()?.toLowerCase() || ''}`));
    if (unsupported.length > 0) {
      setFileError(`Unsupported file type: ${unsupported.map((f) => f.name).join(', ')}`);
      return;
    }
    const emptyFiles = toAdd.filter((f) => f.size === 0);
    if (emptyFiles.length > 0) {
      setFileError(
        emptyFiles.length === 1
          ? `"${emptyFiles[0].name}" is empty and cannot be uploaded.`
          : `${emptyFiles.length} empty files cannot be uploaded.`,
      );
      return;
    }
    const oversized = toAdd.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      setFileError(`Each file must be 10 MB or smaller. Remove: ${oversized.map((f) => f.name).join(', ')}`);
      return;
    }

    setEntries((prev) => [
      ...prev,
      ...toAdd.map((file) => ({ file, description: '' })),
    ]);
  }, [entries.length]);

  const handleDropZoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFilesSelected(e.target.files);
      // Reset input so the same file can be re-added after removal
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFilesSelected],
  );

  const handleRemoveEntry = useCallback((index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleDescriptionChange = useCallback((index: number, value: string) => {
    setEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, description: value } : entry)),
    );
  }, []);

  const handleGenerateWithContext = useCallback(() => {
    onSubmit(
      entries.map((e) => e.file),
      entries.map((e) => e.description),
    );
  }, [entries, onSubmit]);

  const handleGenerateWithoutContext = useCallback(() => {
    onSubmit([], []);
  }, [onSubmit]);

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
          Generate Gherkin Scripts
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
          Your saved Domain Knowledge and Enterprise Knowledge are included automatically.
          Attach files only when you want to add extra, run-specific context—for example,
          a new requirements document, glossary, or API specification.
          Accepted formats: <code>.txt</code>, <code>.md</code>, <code>.markdown</code>, <code>.pdf</code>, <code>.docx</code> (up to 5 files, 10 MB each).
        </p>

        {/* ── Drop zone / file picker ── */}
        <div
          className="border border-dashed rounded p-3 text-center mb-3"
          style={{ cursor: 'pointer', borderStyle: 'dashed', background: '#f9fafb' }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFilesSelected(e.dataTransfer.files);
          }}
        >
          <Upload size={20} className="text-secondary mb-2" />
          <p className="mb-0 text-secondary" style={{ fontSize: '0.85rem' }}>
            Click or drag &amp; drop files here
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES}
            style={{ display: 'none' }}
            onChange={handleDropZoneChange}
          />
        </div>

        {fileError && (
          <p className="text-danger small mb-2">{fileError}</p>
        )}

        {/* ── File list ── */}
        {entries.length > 0 && (
          <div className="d-flex flex-column gap-2">
            {entries.map((entry, idx) => (
              <div
                key={`${entry.file.name}-${idx}`}
                className="border rounded p-2 d-flex align-items-start gap-2"
                style={{ background: '#fff' }}
              >
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                      {entry.file.name}
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      ({formatBytes(entry.file.size)})
                    </span>
                  </div>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Description (optional) — e.g. 'Project glossary'"
                    value={entry.description}
                    onChange={(e) => handleDescriptionChange(idx, e.target.value)}
                  />
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveEntry(idx)}
                  title="Remove file"
                >
                  <Trash size={13} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {entries.length < MAX_FILES && entries.length > 0 && (
          <Button
            variant="link"
            size="sm"
            className="mt-2 p-0 text-decoration-none"
            onClick={() => fileInputRef.current?.click()}
          >
            <PlusCircle size={13} className="me-1" />
            Add another file
          </Button>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          onClick={handleGenerateWithoutContext}
          disabled={submitting}
        >
          Generate
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerateWithContext}
          disabled={submitting || entries.length === 0}
        >
          {entries.length > 0
            ? `Generate With ${entries.length} Additional File${entries.length > 1 ? 's' : ''}`
            : 'Generate With Additional Context'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
